import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { createLesson } from '../../services/api';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import './QuizLessonCreator.css';

const QuizLessonCreator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleRequest, loading } = useApi();
  const [errorMessage, setErrorMessage] = useState('');
  const [lessonData, setLessonData] = useState({
    title: '',
    content: '',
    questionCount: 1,
    lessonLanguage: 'en',
    quizTypes: {
      'multiple-choice': false,
      'true-false': false,
      'short-answer': false,
      'essay': false
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'questionCount') {
      setLessonData(prev => ({
        ...prev,
        [name]: parseInt(value) || 1
      }));
    } else if (name.startsWith('quizTypes.')) {
      const quizType = name.split('.')[1];
      setLessonData(prev => ({
        ...prev,
        quizTypes: {
          ...prev.quizTypes,
          [quizType]: !prev.quizTypes[quizType]
        }
      }));
    } else {
      setLessonData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const selectedQuizTypes = Object.entries(lessonData.quizTypes)
      .filter(([_, isSelected]) => isSelected)
      .map(([type]) => type);

    if (selectedQuizTypes.length === 0) {
      setErrorMessage('Velg minst én quiztype');
      return;
    }

    if (!lessonData.content.trim()) {
      setErrorMessage('Vennligst legg inn leksjonsinnhold');
      return;
    }

    try {
      const formattedData = {
        title: lessonData.title,
        content: lessonData.content,
        questionCount: parseInt(lessonData.questionCount),
        quizTypes: selectedQuizTypes,
        lessonLanguage: lessonData.lessonLanguage || 'en'
      };

      await handleRequest(createLesson, formattedData);
      navigate('/dashboard');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke opprette quizleksjon');
    }
  };

  return (
    <div className="quiz-lesson-creator-container">
      <div className="creator-container">
        <div className="creator-header">
          <button className="back-button" onClick={() => navigate('/dashboard')}>
            ← Tilbake
          </button>
          <h1>Opprett quizleksjon</h1>
        </div>
        <p className="creator-description">
          Opprett en quizleksjon ved å legge inn innhold. AI vil deretter generere spørsmål basert på innholdet.
        </p>

        {errorMessage && <ErrorCard message={errorMessage} />}

        <form onSubmit={handleSubmit} className="creator-form">
          <div className="form-group">
            <label htmlFor="title">Leksjonstittel</label>
            <input
              type="text"
              id="title"
              name="title"
              value={lessonData.title}
              onChange={handleChange}
              placeholder="f.eks. Introduksjon til JavaScript"
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Leksjonsinnhold *</label>
            <textarea
              id="content"
              name="content"
              value={lessonData.content}
              onChange={handleChange}
              placeholder="Skriv inn leksjonsinnholdet her. AI vil generere spørsmål basert på dette innholdet."
              rows="10"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="questionCount">Antall spørsmål</label>
              <input
                type="number"
                id="questionCount"
                name="questionCount"
                value={lessonData.questionCount}
                onChange={handleChange}
                min="1"
                max="20"
                required
              />
            </div>

            <div className="form-group half-width">
              <label htmlFor="lessonLanguage">Språk</label>
              <select
                id="lessonLanguage"
                name="lessonLanguage"
                value={lessonData.lessonLanguage}
                onChange={handleChange}
              >
                <option value="en">Engelsk</option>
                <option value="no">Norsk</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Quiztyper *</label>
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="quizType-multiple-choice"
                  name="quizTypes.multiple-choice"
                  checked={lessonData.quizTypes['multiple-choice']}
                  onChange={handleChange}
                />
                <label htmlFor="quizType-multiple-choice">Flervalg</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="quizType-true-false"
                  name="quizTypes.true-false"
                  checked={lessonData.quizTypes['true-false']}
                  onChange={handleChange}
                />
                <label htmlFor="quizType-true-false">Sant/Usant</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="quizType-short-answer"
                  name="quizTypes.short-answer"
                  checked={lessonData.quizTypes['short-answer']}
                  onChange={handleChange}
                />
                <label htmlFor="quizType-short-answer">Kort svar</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="quizType-essay"
                  name="quizTypes.essay"
                  checked={lessonData.quizTypes['essay']}
                  onChange={handleChange}
                />
                <label htmlFor="quizType-essay">Essay</label>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate('/dashboard')}
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="create-button"
              disabled={loading}
            >
              {loading ? 'Oppretter...' : 'Opprett quizleksjon'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuizLessonCreator;
